import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import useProductStore from '../../stores/useProductStore';

/**
 * Product Modal Component
 * نموذج إضافة/تعديل منتج
 *
 * Protocols Applied:
 * - P-FORM-001: Form Management
 * - P034: Data Validation
 * - P010: Error Handling
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode
 * - P-UX-001: User Experience
 */
const ProductModal = ({ product, onClose }) => {
  const { addProduct, updateProduct } = useProductStore();
  const isEditing = !!product;

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    cost: '',
    stock: '',
    minStock: '10',
    unit: 'علبة',
    barcode: ''
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Popular categories
  const popularCategories = [
    'مشروبات',
    'سناكس',
    'بسكويت',
    'حلويات',
    'معلبات',
    'منظفات',
    'أدوات منزلية',
    'أخرى'
  ];

  // Popular units
  const popularUnits = [
    'علبة',
    'كيس',
    'عبوة',
    'زجاجة',
    'قطعة',
    'كيلو',
    'جرام',
    'لتر'
  ];

  // Load product data if editing
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        category: product.category || '',
        price: product.price || '',
        cost: product.cost || '',
        stock: product.stock || '',
        minStock: product.minStock || '10',
        unit: product.unit || 'علبة',
        barcode: product.barcode || ''
      });
    }
  }, [product]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form (P034)
  const validateForm = () => {
    const newErrors = {};

    // Name is required
    if (!formData.name.trim()) {
      newErrors.name = 'اسم المنتج مطلوب';
    }

    // Category is required
    if (!formData.category.trim()) {
      newErrors.category = 'الفئة مطلوبة';
    }

    // Price validation
    const price = parseFloat(formData.price);
    if (!formData.price || isNaN(price) || price <= 0) {
      newErrors.price = 'السعر يجب أن يكون رقم موجب';
    }

    // Cost validation
    const cost = parseFloat(formData.cost);
    if (!formData.cost || isNaN(cost) || cost < 0) {
      newErrors.cost = 'التكلفة يجب أن تكون رقم موجب أو صفر';
    }

    // Stock validation
    const stock = parseInt(formData.stock);
    if (formData.stock === '' || isNaN(stock) || stock < 0) {
      newErrors.stock = 'المخزون يجب أن يكون رقم موجب أو صفر';
    }

    // MinStock validation
    const minStock = parseInt(formData.minStock);
    if (!formData.minStock || isNaN(minStock) || minStock < 0) {
      newErrors.minStock = 'الحد الأدنى يجب أن يكون رقم موجب أو صفر';
    }

    // Unit is required
    if (!formData.unit.trim()) {
      newErrors.unit = 'الوحدة مطلوبة';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate (P034)
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const productData = {
        name: formData.name.trim(),
        category: formData.category.trim(),
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost),
        stock: parseInt(formData.stock),
        minStock: parseInt(formData.minStock),
        unit: formData.unit.trim(),
        barcode: formData.barcode.trim() || undefined
      };

      if (isEditing) {
        updateProduct(product.id, productData);
      } else {
        addProduct(productData);
      }

      onClose();
    } catch (error) {
      // Error Handling (P010)
      console.error('Error saving product:', error);
      setErrors({ submit: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate profit margin
  const calculateProfitMargin = () => {
    const price = parseFloat(formData.price);
    const cost = parseFloat(formData.cost);

    if (price && cost && cost > 0) {
      return ((price - cost) / cost * 100).toFixed(1);
    }
    return '0';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'تعديل منتج' : 'إضافة منتج جديد'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
              {errors.submit}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Product Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                اسم المنتج <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
                placeholder="مثال: كولا 2 لتر"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الفئة <span className="text-red-500">*</span>
              </label>
              <input
                list="categories"
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={`input w-full ${errors.category ? 'border-red-500' : ''}`}
                placeholder="اختر أو اكتب فئة جديدة"
              />
              <datalist id="categories">
                {popularCategories.map((cat) => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
              {errors.category && (
                <p className="text-red-500 text-sm mt-1">{errors.category}</p>
              )}
            </div>

            {/* Unit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الوحدة <span className="text-red-500">*</span>
              </label>
              <input
                list="units"
                type="text"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                className={`input w-full ${errors.unit ? 'border-red-500' : ''}`}
                placeholder="علبة، كيس، قطعة..."
              />
              <datalist id="units">
                {popularUnits.map((unit) => (
                  <option key={unit} value={unit} />
                ))}
              </datalist>
              {errors.unit && (
                <p className="text-red-500 text-sm mt-1">{errors.unit}</p>
              )}
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                السعر (ج.م) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className={`input w-full ${errors.price ? 'border-red-500' : ''}`}
                placeholder="0.00"
              />
              {errors.price && (
                <p className="text-red-500 text-sm mt-1">{errors.price}</p>
              )}
            </div>

            {/* Cost */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                التكلفة (ج.م) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="cost"
                value={formData.cost}
                onChange={handleChange}
                className={`input w-full ${errors.cost ? 'border-red-500' : ''}`}
                placeholder="0.00"
              />
              {errors.cost && (
                <p className="text-red-500 text-sm mt-1">{errors.cost}</p>
              )}
            </div>

            {/* Profit Margin Display */}
            {formData.price && formData.cost && (
              <div className="md:col-span-2">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    هامش الربح:{' '}
                    <span className="font-bold text-green-700 dark:text-green-400 text-lg">
                      {calculateProfitMargin()}%
                    </span>
                    {' '}
                    (ربح: {(parseFloat(formData.price) - parseFloat(formData.cost)).toFixed(2)} ج.م)
                  </p>
                </div>
              </div>
            )}

            {/* Stock */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                المخزون الحالي <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                className={`input w-full ${errors.stock ? 'border-red-500' : ''}`}
                placeholder="0"
              />
              {errors.stock && (
                <p className="text-red-500 text-sm mt-1">{errors.stock}</p>
              )}
            </div>

            {/* Min Stock */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الحد الأدنى للمخزون
              </label>
              <input
                type="number"
                name="minStock"
                value={formData.minStock}
                onChange={handleChange}
                className={`input w-full ${errors.minStock ? 'border-red-500' : ''}`}
                placeholder="10"
              />
              {errors.minStock && (
                <p className="text-red-500 text-sm mt-1">{errors.minStock}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                سيتم تنبيهك عندما ينخفض المخزون عن هذا الحد
              </p>
            </div>

            {/* Barcode */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                الباركود (اختياري)
              </label>
              <input
                type="text"
                name="barcode"
                value={formData.barcode}
                onChange={handleChange}
                className="input w-full"
                placeholder="123456789012"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'جارٍ الحفظ...' : isEditing ? 'حفظ التعديلات' : 'إضافة المنتج'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 btn btn-secondary disabled:opacity-50"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;
